import EventEmitter from 'events';
import { GraphQLClient } from 'graphql-request';
import type { Variables, RequestDocument } from 'graphql-request';
import { compare as semverCompare } from 'semver';
import { Disposable } from 'vscode-languageserver-protocol';
import { Cable as ActionCableCable } from '@anycable/core';
import { Injectable, createInterfaceId } from '@gitlab-org/di';
import { ConfigService } from './config_service';
import { ICodeSuggestionModel, IClientInfo } from './tracking/tracking_types';
import { LsFetch } from './fetch';
import { log } from './log';
import { handleFetchError } from './handle_fetch_error';
import { getLanguageServerVersion } from './utils/get_language_server_version';
import { GITLAB_API_BASE_URL } from './constants';
import { AdditionalContext, SuggestionOption, ApiRequest } from './api_types';
import { createQueryString, QueryValue } from './utils/create_query_string';
import { InvalidInstanceVersionError } from './fetch_error';
import { connectToCable } from './action_cable';

export interface ApiReconfiguredData {
  /** is the API client configured in a way that allows it to send request to the API */
  isInValidState: boolean;
  validationMessage?: string;
}

export interface GitLabApiClient {
  checkToken(token: string | undefined): Promise<TokenCheckResponse>;
  getCodeSuggestions(request: CodeSuggestionRequest): Promise<CodeSuggestionResponse | undefined>;
  getStreamingCodeSuggestions(request: CodeSuggestionRequest): AsyncGenerator<string, void, void>;
  fetchFromApi<TReturnType>(request: ApiRequest<TReturnType>): Promise<TReturnType>;
  connectToCable(): Promise<ActionCableCable>;
  onApiReconfigured(listener: (data: ApiReconfiguredData) => void): Disposable;
  readonly instanceVersion?: string;
}

export const GitLabApiClient = createInterfaceId<GitLabApiClient>('GitLabApiClient');

export type GenerationType = 'comment' | 'small_file' | 'empty_function' | undefined;

export interface CodeSuggestionRequest {
  prompt_version: number;
  project_path: string;
  model_provider?: string;
  project_id: number;
  current_file: CodeSuggestionRequestCurrentFile;
  intent?: 'completion' | 'generation';
  stream?: boolean;
  /** how many suggestion options should the API return */
  choices_count?: number;
  /** additional context to provide to the model */
  context?: AdditionalContext[];
  /* A user's instructions for code suggestions. */
  user_instruction?: string;
  /* The backend can add additional prompt info based on the type of event */
  generation_type?: GenerationType;
}

export interface CodeSuggestionRequestCurrentFile {
  file_name: string;
  content_above_cursor: string;
  content_below_cursor: string;
}

export interface CodeSuggestionResponse {
  choices?: SuggestionOption[];
  model?: ICodeSuggestionModel;
  status: number;
  error?: string;
  isDirectConnection?: boolean;
}

// FIXME: Rename to SuggestionOptionStream when renaming the SuggestionOption
export interface StartStreamOption {
  uniqueTrackingId: string;
  /** the streamId represents the beginning of a stream * */
  streamId: string;
}

export type SuggestionOptionOrStream = SuggestionOption | StartStreamOption;

export interface PersonalAccessToken {
  name: string;
  scopes: string[];
  active: boolean;
}

export interface OAuthToken {
  scope: string[];
}

export interface TokenCheckResponse {
  valid: boolean;
  reason?: 'unknown' | 'not_active' | 'invalid_scopes';
  message?: string;
}

export interface IDirectConnectionDetailsHeaders {
  'X-Gitlab-Global-User-Id': string;
  'X-Gitlab-Instance-Id': string;
  'X-Gitlab-Host-Name': string;
  'X-Gitlab-Saas-Duo-Pro-Namespace-Ids': string;
}

export interface IDirectConnectionModelDetails {
  model_provider: string;
  model_name: string;
}

export interface IDirectConnectionDetails {
  base_url: string;
  token: string;
  expires_at: number;
  headers: IDirectConnectionDetailsHeaders;
  model_details: IDirectConnectionModelDetails;
}

const CONFIG_CHANGE_EVENT_NAME = 'apiReconfigured';

@Injectable(GitLabApiClient, [LsFetch, ConfigService])
export class GitLabAPI implements GitLabApiClient {
  #token: string | undefined;

  #baseURL: string;

  #clientInfo?: IClientInfo;

  #lsFetch: LsFetch;

  #eventEmitter = new EventEmitter();

  #configService: ConfigService;

  #instanceVersion?: string;

  constructor(lsFetch: LsFetch, configService: ConfigService) {
    this.#baseURL = GITLAB_API_BASE_URL;
    this.#lsFetch = lsFetch;
    this.#configService = configService;
    this.#configService.onConfigChange(async (config) => {
      this.#clientInfo = configService.get('client.clientInfo');
      this.#lsFetch.updateAgentOptions({
        ignoreCertificateErrors: this.#configService.get('client.ignoreCertificateErrors') ?? false,
        ...(this.#configService.get('client.httpAgentOptions') ?? {}),
      });
      await this.configureApi(config.client);
    });
  }

  onApiReconfigured(listener: (data: ApiReconfiguredData) => void): Disposable {
    this.#eventEmitter.on(CONFIG_CHANGE_EVENT_NAME, listener);
    return { dispose: () => this.#eventEmitter.removeListener(CONFIG_CHANGE_EVENT_NAME, listener) };
  }

  #fireApiReconfigured(isInValidState: boolean, validationMessage?: string) {
    const data: ApiReconfiguredData = { isInValidState, validationMessage };
    this.#eventEmitter.emit(CONFIG_CHANGE_EVENT_NAME, data);
  }

  async configureApi({
    token,
    baseUrl = GITLAB_API_BASE_URL,
  }: {
    token?: string;
    baseUrl?: string;
  }) {
    if (this.#token === token && this.#baseURL === baseUrl) return;
    this.#token = token;
    this.#baseURL = baseUrl;
    const { valid, reason, message } = await this.checkToken(this.#token);
    let validationMessage;
    if (!valid) {
      this.#configService.set('client.token', undefined);

      validationMessage = `Token is invalid. ${message}. Reason: ${reason}`;
      log.warn(validationMessage);
    } else {
      log.info('Token is valid');
    }

    this.#instanceVersion = await this.#getGitLabInstanceVersion();

    this.#fireApiReconfigured(valid, validationMessage);
  }

  #looksLikePatToken(token: string): boolean {
    // OAuth tokens will be longer than 42 characters and PATs will be shorter.
    return token.length < 42;
  }

  async #checkPatToken(token: string): Promise<TokenCheckResponse> {
    const headers = this.#getDefaultHeaders(token);

    const response = await this.#lsFetch.get(
      `${this.#baseURL}/api/v4/personal_access_tokens/self`,
      { headers },
    );

    await handleFetchError(response, 'Information about personal access token');

    const { active, scopes } = (await response.json()) as PersonalAccessToken;

    if (!active) {
      return {
        valid: false,
        reason: 'not_active',
        message: 'Token is not active.',
      };
    }

    if (!this.#hasValidScopes(scopes)) {
      const joinedScopes = scopes.map((scope) => `'${scope}'`).join(', ');

      return {
        valid: false,
        reason: 'invalid_scopes',
        message: `Token has scope(s) ${joinedScopes} (needs 'api').`,
      };
    }

    return { valid: true };
  }

  async #checkOAuthToken(token: string): Promise<TokenCheckResponse> {
    const headers = this.#getDefaultHeaders(token);

    const response = await this.#lsFetch.get(`${this.#baseURL}/oauth/token/info`, {
      headers,
    });
    await handleFetchError(response, 'Information about OAuth token');

    const { scope: scopes } = (await response.json()) as OAuthToken;
    if (!this.#hasValidScopes(scopes)) {
      const joinedScopes = scopes.map((scope) => `'${scope}'`).join(', ');

      return {
        valid: false,
        reason: 'invalid_scopes',
        message: `Token has scope(s) ${joinedScopes} (needs 'api').`,
      };
    }

    return { valid: true };
  }

  async checkToken(token: string = ''): Promise<TokenCheckResponse> {
    try {
      if (this.#looksLikePatToken(token)) {
        log.info('Checking token for PAT validity');
        return await this.#checkPatToken(token);
      }
      log.info('Checking token for OAuth validity');
      return await this.#checkOAuthToken(token);
    } catch (err) {
      log.error('Error performing token check', err);
      return {
        valid: false,
        reason: 'unknown',
        message: `Failed to check token: ${err}`,
      };
    }
  }

  #hasValidScopes(scopes: string[]): boolean {
    return scopes.includes('api');
  }

  async getCodeSuggestions(
    request: CodeSuggestionRequest,
  ): Promise<CodeSuggestionResponse | undefined> {
    if (!this.#token) {
      throw new Error('Token needs to be provided to request Code Suggestions');
    }

    const headers = {
      ...this.#getDefaultHeaders(this.#token),
      'Content-Type': 'application/json',
    };

    const response = await this.#lsFetch.post(
      `${this.#baseURL}/api/v4/code_suggestions/completions`,
      { headers, body: JSON.stringify(request) },
    );

    await handleFetchError(response, 'Code Suggestions');

    const data = await response.json();
    return { ...data, status: response.status };
  }

  async *getStreamingCodeSuggestions(
    request: CodeSuggestionRequest,
  ): AsyncGenerator<string, void, void> {
    if (!this.#token) {
      throw new Error('Token needs to be provided to stream code suggestions');
    }

    const headers = {
      ...this.#getDefaultHeaders(this.#token),
      'Content-Type': 'application/json',
    };

    yield* this.#lsFetch.streamFetch(
      `${this.#baseURL}/api/v4/code_suggestions/completions`,
      JSON.stringify(request),
      headers,
    );
  }

  async fetchFromApi<TReturnType>(request: ApiRequest<TReturnType>): Promise<TReturnType> {
    if (!this.#token) {
      return Promise.reject(new Error('Token needs to be provided to authorise API request.'));
    }

    if (
      request.supportedSinceInstanceVersion &&
      !this.#instanceVersionHigherOrEqualThen(request.supportedSinceInstanceVersion.version)
    ) {
      return Promise.reject(
        new InvalidInstanceVersionError(
          `Can't ${request.supportedSinceInstanceVersion.resourceName} until your instance is upgraded to ${request.supportedSinceInstanceVersion.version} or higher.`,
        ),
      );
    }

    if (request.type === 'graphql') {
      return this.#graphqlRequest(request.query, request.variables);
    }
    switch (request.method) {
      case 'GET':
        return this.#fetch(request.path, request.searchParams, 'resource', request.headers);
      case 'POST':
        return this.#postFetch(request.path, 'resource', request.body, request.headers);
      default:
        // the type assertion is necessary because TS doesn't expect any other types
        throw new Error(`Unknown request type ${(request as ApiRequest<unknown>).type}`);
    }
  }

  async connectToCable(): Promise<ActionCableCable> {
    const headers = this.#getDefaultHeaders(this.#token);
    const websocketOptions = {
      headers: {
        ...headers,
        Origin: this.#baseURL,
      },
    };

    return connectToCable(this.#baseURL, websocketOptions);
  }

  async #graphqlRequest<T = unknown, V extends Variables = Variables>(
    document: RequestDocument,
    variables?: V,
  ): Promise<T> {
    const ensureEndsWithSlash = (url: string) => url.replace(/\/?$/, '/');
    const endpoint = new URL('./api/graphql', ensureEndsWithSlash(this.#baseURL)).href; // supports GitLab instances that are on a custom path, e.g. "https://example.com/gitlab"
    const graphqlFetch = async (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> => {
      const url = input instanceof URL ? input.toString() : input;
      return this.#lsFetch.post(url, {
        ...init,
        headers: { ...headers, ...init?.headers },
      });
    };

    const headers = this.#getDefaultHeaders(this.#token);
    const client = new GraphQLClient(endpoint, {
      headers,
      fetch: graphqlFetch,
    });
    return client.request(document, variables);
  }

  async #fetch<T>(
    apiResourcePath: string,
    query: Record<string, QueryValue> = {},
    resourceName = 'resource',
    headers?: Record<string, string>,
  ): Promise<T> {
    const url = `${this.#baseURL}/api/v4${apiResourcePath}${createQueryString(query)}`;

    const result = await this.#lsFetch.get(url, {
      headers: { ...this.#getDefaultHeaders(this.#token), ...headers },
    });

    await handleFetchError(result, resourceName);

    return result.json() as Promise<T>;
  }

  async #postFetch<T>(
    apiResourcePath: string,
    resourceName = 'resource',
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<T> {
    const url = `${this.#baseURL}/api/v4${apiResourcePath}`;

    const response = await this.#lsFetch.post(url, {
      headers: {
        'Content-Type': 'application/json',
        ...this.#getDefaultHeaders(this.#token),
        ...headers,
      },
      body: JSON.stringify(body),
    });

    await handleFetchError(response, resourceName);

    return response.json() as Promise<T>;
  }

  #getDefaultHeaders(token?: string) {
    return {
      Authorization: `Bearer ${token}`,
      'User-Agent': `code-completions-language-server-experiment (${this.#clientInfo?.name}:${this.#clientInfo?.version})`,
      'X-Gitlab-Language-Server-Version': getLanguageServerVersion(),
    };
  }

  #instanceVersionHigherOrEqualThen(version: string): boolean {
    if (!this.#instanceVersion) return false;

    return semverCompare(this.#instanceVersion, version) >= 0;
  }

  async #getGitLabInstanceVersion(): Promise<string> {
    if (!this.#token) {
      return '';
    }

    const headers = this.#getDefaultHeaders(this.#token);

    const response = await this.#lsFetch.get(`${this.#baseURL}/api/v4/version`, {
      headers,
    });
    const { version } = await response.json();

    return version;
  }

  get instanceVersion() {
    return this.#instanceVersion;
  }
}
